// Several controls tested over one sample.
//
// A firm draws one sample of journals for the period and runs every journal control over it, rather
// than re-drawing a sample per control. The sample is the shared thing; the tests are not. Each
// control still concludes for itself, which is why they can disagree.
//
// Titles and taglines track the design document so a walkthrough can follow it section by section.
import {BasisItem, Conclusion, DemoControl, Scenario} from "../model";
import {effective, withExceptions} from "./cast";

const FY26 = {from: "2026-01-01", to: "2026-12-31"};

/** A daily journal control, sampled rather than observed. */
const journalControl = (ref: string, title: string, oe: Conclusion): DemoControl => ({
  ref,
  title,
  assessment: "Financial Controls",
  frequency: "daily",
  scopes: [{scope: "Global", design: "effective", implementation: "effective", oe}],
});

const test = (label: string, exceptions: number, conclusion: string): BasisItem => ({
  id: "b1",
  span: FY26,
  status: "obtained",
  evidence: {
    kind: "test",
    label,
    itemsTested: 25,
    exceptions,
    outcome: exceptions > 0 ? "operated_with_exceptions" : "operated_effectively",
    conclusion,
  },
  conditions: [],
});

const authorisation = effective(
  "All 25 journals in the sample carried an approver distinct from the preparer, recorded before the " +
    "journal was posted.",
);

const segregation = withExceptions(
  "One of the 25 journals was released by the person who raised it, during a period of leave cover. " +
    "The amount was immaterial and the entry was subsequently reviewed, so the exception is not " +
    "considered to indicate a breakdown in the control.",
);

const support = effective("Supporting documentation was located for all 25 journals in the sample.");

export const sharedSample: Scenario = {
  id: "s10",
  section: "Scenario 10",
  title: "One sample, three controls",
  tagline: "the sample is shared, the conclusions aren't",
  situation:
    "The journal population is sampled once for the period — 25 items — and all three journal " +
    "controls are tested over that one sample. Nobody re-draws a sample per control, and each " +
    "control still reaches its own conclusion: two effective, one with an exception.",
  focus: {control: "JE-01", scope: "Global"},
  controls: [
    journalControl("JE-01", "Manual journal authorisation", authorisation),
    journalControl("JE-03", "Segregation of duties over journals", segregation),
    journalControl("JE-07", "Journal supporting documentation review", support),
  ],
  edges: [],
  oe: [
    {
      control: "JE-01",
      scope: "Global",
      period: FY26,
      basis: [test("JE-01 journal authorisation", 0, authorisation.justification)],
      conclusion: authorisation,
    },
    {
      control: "JE-03",
      scope: "Global",
      period: FY26,
      basis: [test("JE-03 segregation of duties", 1, segregation.justification)],
      conclusion: segregation,
    },
    {
      control: "JE-07",
      scope: "Global",
      period: FY26,
      basis: [test("JE-07 supporting documentation", 0, support.justification)],
      conclusion: support,
    },
  ],
};
